import { DayPatternEntry, WEEKDAY_NAMES, toMinutePrecision } from "./day-pattern";

/**
 * A pattern entry as `GET /job/:id` sends it. Times come out of a Postgres
 * `time` column as "HH:mm:ss", and `employeeIds` is absent on a day the
 * synthesis found no crew for.
 */
export type ApiDayPatternEntry = {
  dayOfWeek: number;
  startTime?: string;
  endTime?: string;
  employeeIds?: string[];
};

/**
 * Why a day the pattern asked for produced no shift.
 *
 * `dayAlreadyScheduled` - the job already has a visit that calendar day, so the
 * day is full whatever hours were asked for. `overlapsKeptShift` - the day was
 * free and only these particular hours clashed, which a different start time
 * fixes. The two read differently to a scheduler, which is why the API sends
 * them apart.
 */
export type SkippedDayReason = "dayAlreadyScheduled" | "overlapsKeptShift";

/** Why the occurrence that displaced it survived the regeneration. */
export type KeptOccurrenceReason = "started" | "checkedIn" | "invoiced";

/**
 * A day the regeneration dropped. Dates and times are already in the JOB's
 * zone - "YYYY-MM-DD" and "HH:mm" - so nothing here may be fed to `new Date`:
 * the browser's zone is not the job's, and an ISO date parsed as UTC midnight
 * renders as the previous day across the Americas.
 */
export type SkippedPatternDay = {
  date: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  reason: SkippedDayReason;
  keptBy: {
    scheduleId: number;
    date: string;
    startTime: string;
    endTime: string;
    reason: KeptOccurrenceReason;
  };
};

/** What `PUT /job/:id/series` answers with. */
export type PatternSaveCounts = {
  removed: number;
  created: number;
  /**
   * Future occurrences kept because something depends on them - a check-in or
   * an invoice. Started and past occurrences are never touched and never
   * counted, so this is only ever about shifts still to come.
   */
  preserved: number;
  /**
   * Days the new pattern asked for and the regeneration did not create. Sent as
   * an empty array when there were none; optional here only because a response
   * from an older API carries no such field at all, and an absent one has to
   * read as "nothing to report" rather than crash the save it belongs to.
   */
  skipped?: SkippedPatternDay[];
};

/**
 * The API's pattern, in the shape the cards edit.
 *
 * Deliberately not routed through syncPatternWithDays: that rebuilds the
 * pattern from a Set keyed by weekday and so dedupes it, and a repeated weekday
 * is the one problem only an API-loaded pattern can carry -
 * describeInvalidPatternDays exists to flag it on the card. Order is kept for
 * the same reason: the cards are keyed by index, and the repeat has to stay
 * where the server put it.
 *
 * Times are cut to minute precision on the way in, before they can reach the
 * DTO's /^([01]\d|2[0-3]):[0-5]\d$/ - or be compared against a time the native
 * <input type="time"> produced, where "17:00" < "17:00:00" lexicographically.
 */
export const toLoadedDayPattern = (
  dayPattern?: ApiDayPatternEntry[] | null
): DayPatternEntry[] =>
  (dayPattern ?? []).map((entry) => ({
    dayOfWeek: Number(entry.dayOfWeek),
    startTime: toMinutePrecision(entry.startTime ?? ""),
    endTime: toMinutePrecision(entry.endTime ?? ""),
    // A fresh array per entry: two cards sharing one would move employees
    // between days.
    employeeIds: [...(entry.employeeIds ?? [])].map(String),
  }));

/**
 * The weekday chips a loaded pattern implies. The chips are derived from the
 * pattern rather than read from the job, so the two can never disagree - a lit
 * chip with no card underneath it is unreachable from a load.
 */
export const daysOfWeekFromPattern = (pattern: DayPatternEntry[]): number[] =>
  Array.from(new Set(pattern.map((entry) => entry.dayOfWeek))).sort((a, b) => a - b);

/**
 * An occurrence as `GET /job/:id` sends it, reduced to the fields the edit
 * screen seeds its per-occurrence controls from.
 *
 * `supervisor` is the API's own split of the schedule's people: the server
 * derives it from the flagged `isSupervisor` link rows, with no fall back to
 * the division's supervisors, so it is a faithful mirror of who is named ON
 * that occurrence - which is what makes the rule below computable here at all.
 */
export type ApiJobSchedule = {
  startTime?: string | null;
  zoneCode?: number | null;
  lateInAlertTime?: number | null;
  lateOutAlertTime?: number | null;
  supervisor?: { id: string }[] | null;
};

const byStartTime = (schedules: ApiJobSchedule[]): ApiJobSchedule[] =>
  [...schedules].sort(
    (left, right) =>
      new Date(left.startTime ?? 0).getTime() - new Date(right.startTime ?? 0).getTime()
  );

/**
 * The occurrence a series save would copy the division and the alert times
 * from if this edit changed neither: the latest FUTURE one, or failing that the
 * latest of all.
 *
 * It mirrors `DayPatternService.regenerate`'s own `template` rule exactly, and
 * that is the whole point. Seeding the controls from `jobSchedules[0]` - which
 * is whatever order the query returned, and `GET /job/:id` puts no ORDER BY on
 * the relation - showed one division on screen while an untouched save kept a
 * different one, with nothing to tell the user which was real.
 */
export const seriesTemplateSchedule = (
  schedules?: ApiJobSchedule[] | null,
  now: Date = new Date()
): ApiJobSchedule | undefined => {
  const ordered = byStartTime(schedules ?? []);
  const future = ordered.filter(
    (schedule) => new Date(schedule.startTime ?? 0).getTime() > now.getTime()
  );

  // Written as regenerate writes it, and the `future` half is an equivalent
  // restatement: once `ordered` is sorted, a non-empty `future` always ends on
  // the series' latest occurrence, which is `ordered`'s last element too. Kept
  // so the two sides read the same, and mutation-tested as equivalent -
  // dropping the `future` half changes nothing, while dropping the FALLBACK
  // breaks every job whose occurrences are all in the past.
  return future[future.length - 1] ?? ordered[ordered.length - 1];
};

/**
 * Whom the series currently names as supervisors: the ids on the LATEST
 * occurrence that names any, never a union across the series.
 *
 * The same rule the server inherits by, and for the same reason - a union
 * brings back somebody who was replaced mid-series. It matters here even though
 * the server would apply that rule itself, because the moment the user touches
 * the Supervisors control the WHOLE list on screen is sent and the server's
 * rule is bypassed: seeded from the oldest occurrence, adding one person would
 * quietly reinstate a supervisor who had been replaced.
 *
 * The whole series, not just the future part, exactly as regenerate reads it: a
 * job whose remaining occurrences are all in the past would otherwise show no
 * supervisors at all.
 */
export const inheritedSupervisorIds = (schedules?: ApiJobSchedule[] | null): string[] => {
  const latestNaming = byStartTime(schedules ?? [])
    .reverse()
    .find((schedule) => (schedule.supervisor ?? []).length > 0);

  return (latestNaming?.supervisor ?? []).map((supervisor) => String(supervisor.id));
};

/**
 * Compared at minute precision and with each day's crew sorted: the pattern
 * that came back from the API carries seconds, and the MultiSelect reports its
 * value in selection order, so neither difference means the user changed
 * anything. Entry order itself is significant - the cards are index-keyed.
 */
const fingerprint = (pattern: DayPatternEntry[]): string =>
  JSON.stringify(
    pattern.map((entry) => [
      entry.dayOfWeek,
      toMinutePrecision(entry.startTime ?? ""),
      toMinutePrecision(entry.endTime ?? ""),
      [...(entry.employeeIds ?? [])].sort(),
    ])
  );

/**
 * Whether saving has anything to do. Regeneration deletes and recreates
 * occurrences, so an unchanged pattern must not be sent: it would churn every
 * upcoming shift for nothing.
 */
export const hasPatternChanged = (
  loaded: DayPatternEntry[],
  current: DayPatternEntry[]
): boolean => fingerprint(loaded) !== fingerprint(current);

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * The counts, said the way the user would say them. `preserved` is reported
 * with its reason, because a shift the save left alone looks like a bug
 * otherwise - and it is omitted entirely when there was none, rather than
 * announcing a zero.
 */
export const describePatternSaveResult = ({
  removed = 0,
  created = 0,
  preserved = 0,
}: Partial<PatternSaveCounts>): string => {
  if (!removed && !created) {
    return "Day pattern saved. No upcoming shifts needed changing.";
  }

  const rescheduled = `Day pattern updated. ${plural(created, "upcoming shift")} rescheduled`;

  if (!preserved) {
    return `${rescheduled}.`;
  }

  return `${rescheduled}, ${preserved} left unchanged because ${
    preserved === 1 ? "it has" : "they have"
  } already been checked into or invoiced.`;
};

const KEPT_PHRASES: Record<KeptOccurrenceReason, string> = {
  started: "a shift that has already started",
  checkedIn: "a shift that has already been checked into",
  invoiced: "a shift that has already been invoiced",
};

const describeSkippedDay = (day: SkippedPatternDay): string => {
  // The weekday name comes off the API's own dayOfWeek, not off a parsed date:
  // the date string is the job's local day and deriving a weekday from it in the
  // browser would name the wrong one for any job east or west of the user.
  const when = `${WEEKDAY_NAMES[day.dayOfWeek] ?? "That day"} ${day.date}, ${
    day.startTime
  }-${day.endTime}`;
  const kept = KEPT_PHRASES[day.keptBy.reason] ?? "an existing shift";

  if (day.reason === "overlapsKeptShift") {
    return `${when} was not created: it would have overlapped ${kept} on ${day.keptBy.date}, ${day.keptBy.startTime}-${day.keptBy.endTime}.`;
  }

  // The one-visit-per-day rule belongs to THIS branch only. Said over an
  // overlap it would be false - that day was free and only these hours clashed -
  // and it would send the scheduler looking for a same-day visit that is not
  // there.
  return `${when} was not created: a job can only have one visit per day, and that day already has ${kept} at ${day.keptBy.startTime}-${day.keptBy.endTime}.`;
};

/**
 * The days the save dropped, in the scheduler's words.
 *
 * Empty string when there were none, so the caller can use it as the test for
 * whether there is anything to warn about at all. This is the part a scheduler
 * cannot be allowed to miss: moving Thursday to the afternoon over an invoiced
 * morning leaves Thursday exactly where it was, and every count in the response
 * still reads like an ordinary successful save.
 */
export const describeSkippedPatternDays = (
  skipped?: SkippedPatternDay[] | null
): string => {
  if (!skipped?.length) {
    return "";
  }

  // The header counts and does not explain: the two reasons do not share one,
  // and each sentence below carries its own.
  return `${plural(skipped.length, "day")} in the new pattern did not take. ${skipped
    .map(describeSkippedDay)
    .join(" ")}`;
};

/**
 * Said when a gateway gave up waiting, or the request was aborted client-side.
 * The save may have landed before the reply was lost, so the user is told to
 * reload rather than retry blind - a retry on top of a save that succeeded
 * would double the work.
 */
export const PATTERN_SAVE_TIMEOUT_MESSAGE =
  "The server took too long to respond — the schedule may still have been updated; reload before retrying";

/**
 * Said when the request left the browser and no reply came back at all, which
 * is what a dropped connection looks like from here.
 */
export const PATTERN_SAVE_NO_RESPONSE_MESSAGE =
  "Could not reach the server — the schedule may still have been updated; reload before retrying";

/**
 * The server's own words where there are any. Every 400 this endpoint raises
 * names something the user can act on - a non-weekly job, a missing end date, a
 * pattern that would leave the job with no shifts at all - and a generic
 * "Something went wrong" would throw exactly that away. Read from both shapes:
 * this API wraps handled errors under `error.message`, while the DTO's own
 * validation failures arrive as Nest's `message`, which is a list.
 */
const serverMessage = (error: any): string | null => {
  const data = error?.response?.data;
  const wrapped = data?.error?.message;

  if (typeof wrapped === "string" && wrapped) {
    return wrapped;
  }

  const message = data?.message;

  if (Array.isArray(message) && message.length) {
    return message.join(", ");
  }

  if (typeof message === "string" && message) {
    return message;
  }

  return null;
};

/**
 * What a save that never got a usable answer should say. A 502, 503 or 504
 * means a proxy in front of the API gave up, and an ECONNABORTED means the
 * client did; both are timeouts. Only a request that went out and heard nothing
 * back is a dropped connection. Checked only after the server's own words, so
 * a refusal the API did spell out is never replaced by a guess about the network.
 */
const transportMessage = (error: any): string | null => {
  const status = error?.response?.status;

  if (status === 502 || status === 503 || status === 504 || error?.code === "ECONNABORTED") {
    return PATTERN_SAVE_TIMEOUT_MESSAGE;
  }

  if (error?.request && !error?.response) {
    return PATTERN_SAVE_NO_RESPONSE_MESSAGE;
  }

  return null;
};

export const describePatternSaveError = (error: any): string =>
  serverMessage(error) ??
  transportMessage(error) ??
  "Could not update the day pattern";

/**
 * The same reading, with the fallback that names the right request. The two
 * calls the edit save makes fail differently, and telling someone whose name
 * edit was refused that the day pattern could not be updated sends them to
 * the wrong control.
 */
export const describeJobDetailSaveError = (error: any): string =>
  serverMessage(error) ?? transportMessage(error) ?? "Could not save the job's details";

/**
 * Everything `PUT /job/:id/series` can change, as the form holds it.
 *
 * The weekday pattern is only one of the three: the endpoint was renamed from
 * `/day-pattern` precisely because the recurrence interval and the series' end
 * date go up with it, and all three regenerate the same occurrences.
 */
export type SeriesTerms = {
  dayPattern: DayPatternEntry[];
  recurEvery: number | null;
  /** The form's `recurrenceEndDate`; the body's `jobEndTime`. */
  recurrenceEndDate: Date | null;
  /**
   * The four fields that belong to each OCCURRENCE rather than to the job, and
   * which the series route now applies to the occurrences it regenerates: who
   * supervises them, which division they belong to and the two alert times.
   *
   * They are part of the terms because a change to any one of them is a reason
   * to regenerate - and because `hasSeriesChanged` is the only thing standing
   * between an untouched Save and a year of churned shifts.
   *
   * `supervisorIds` is `[]`-significant all the way down: empty means "the
   * Service Division's own", which is what the control's placeholder says and
   * what the endpoint does with an empty list. The other three have no cleared
   * state - the division is NOT NULL, and the two alert Dropdowns have no
   * showClear, so an empty value cannot be produced from the screen.
   */
  supervisorIds: string[];
  zoneCode: number | null;
  lateInAlertTime: number | null;
  lateOutAlertTime: number | null;
};

/**
 * Supervisor ids as an identity rather than as a list. The MultiSelect reports
 * its value in selection order, so re-picking the same two people in the other
 * order is not a change - exactly the reason each day's crew is sorted inside
 * `fingerprint`.
 */
const supervisorFingerprint = (ids?: string[] | null): string =>
  JSON.stringify([...(ids ?? [])].sort());

const endDateValue = (value: Date | string | null | undefined): number | null => {
  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

/**
 * Whether saving the series has anything to do. Compared BY VALUE throughout:
 * yup casts the submitted values, so the end date arrives as a different Date
 * object holding the same instant, and a reference comparison would report
 * every save as a change - regenerating a year of occurrences for nothing.
 */
export const hasSeriesChanged = (loaded: SeriesTerms, current: SeriesTerms): boolean =>
  hasPatternChanged(loaded.dayPattern, current.dayPattern) ||
  (loaded.recurEvery ?? null) !== (current.recurEvery ?? null) ||
  endDateValue(loaded.recurrenceEndDate) !== endDateValue(current.recurrenceEndDate) ||
  // The four per-occurrence fields count as changes too. Without them, editing
  // only the Supervisors control reports "No changes to the day pattern." and
  // throws the edit away - which is how a field that is wired up everywhere
  // else still does nothing.
  hasOccurrenceTermsChanged(loaded, current);

/**
 * Whether any of the four per-occurrence terms moved. Separate from
 * `hasSeriesChanged` because the save needs the same answer per FIELD - each is
 * sent only when it differs - and one predicate asked four ways is better than
 * four written twice.
 */
export const hasOccurrenceTermsChanged = (
  loaded: SeriesTerms,
  current: SeriesTerms
): boolean =>
  changedOccurrenceTerms(loaded, current).length > 0;

/** The per-occurrence terms this save should send, and no others. */
export type OccurrenceTerm =
  | "supervisorIds"
  | "zoneCode"
  | "lateInAlertTime"
  | "lateOutAlertTime";

/**
 * Which of the four to send.
 *
 * Only the ones that MOVED, never all four on every save, and that is not an
 * optimisation. The form seeds these from `jobSchedules[0]`, while regeneration
 * inherits the division and the alert times from the latest FUTURE occurrence
 * and the supervisors from the latest occurrence that names any. The two need
 * not agree - `GET /job/:id` puts no ORDER BY on the relation - so a save that
 * posted all four unconditionally could stamp one occurrence's values over a
 * series that had since moved on, for a field the user never touched. Sending
 * only what changed means an untouched field keeps whatever the server inherits.
 */
export const changedOccurrenceTerms = (
  loaded: SeriesTerms,
  current: SeriesTerms
): OccurrenceTerm[] => {
  const changed: OccurrenceTerm[] = [];

  if (supervisorFingerprint(loaded.supervisorIds) !== supervisorFingerprint(current.supervisorIds)) {
    changed.push("supervisorIds");
  }

  // `?? null` on both sides so that an absent field and an explicit null are
  // one value here, the way they are one value to the endpoint.
  //
  // A move TO null is not a change, for all three. None of them has a cleared
  // state - the division column is NOT NULL and the two alert Dropdowns carry
  // no showClear - so a null here is a job that loaded without the field, not a
  // user clearing it. Counted as a change it would mark the series dirty, send
  // nothing (the endpoint reads a null as "not supplied"), and regenerate a
  // year of shifts to arrive back where it started.
  const moved = (before: number | null, after: number | null) =>
    after != null && (before ?? null) !== after;

  if (moved(loaded.zoneCode ?? null, current.zoneCode ?? null)) {
    changed.push("zoneCode");
  }

  if (moved(loaded.lateInAlertTime ?? null, current.lateInAlertTime ?? null)) {
    changed.push("lateInAlertTime");
  }

  if (moved(loaded.lateOutAlertTime ?? null, current.lateOutAlertTime ?? null)) {
    changed.push("lateOutAlertTime");
  }

  return changed;
};

/** Whether the series' end was moved EARLIER - the case the server misadvises. */
export const isEndDateShortened = (loaded: SeriesTerms, current: SeriesTerms): boolean => {
  const before = endDateValue(loaded.recurrenceEndDate);
  const after = endDateValue(current.recurrenceEndDate);

  return before !== null && after !== null && after < before;
};

/**
 * The fields `PUT /job/jobDetail/:id` can change that this form actually lets
 * anyone edit.
 *
 * Deliberately NOT the whole of UpdateJobDetailDto: `jobType` is a disabled
 * input AND absent from the DTO, `attachment2Path` holds File objects here and
 * would serialize to `[{}]`, and `paymentsTerm` has no control on this screen.
 */
export type JobDetailFields = {
  name: string;
  additionalNotes: string;
  invoiceGeneration: string;
  /**
   * Nullable, and null is MEANINGFUL here: the column is nullable and the
   * service spreads the body into the update, so a null clears it. That is the
   * opposite of the series route, where a null reads as "not supplied".
   */
  laborGoalHours: number | null;
};

export const pickJobDetailFields = ({
  name,
  additionalNotes,
  invoiceGeneration,
  laborGoalHours,
}: {
  name?: string | null;
  additionalNotes?: string | null;
  invoiceGeneration?: string | null;
  laborGoalHours?: number | string | null;
}): JobDetailFields => ({
  name: name ?? "",
  additionalNotes: additionalNotes ?? "",
  invoiceGeneration: invoiceGeneration ?? "",
  // The column is `numeric`, which some drivers hand back as a string.
  laborGoalHours:
    laborGoalHours === null || laborGoalHours === undefined || laborGoalHours === ""
      ? null
      : Number(laborGoalHours),
});

/**
 * Whether the job-level save has anything to do. The update spreads the body
 * straight into the repository, so an unchanged field is better left unsent
 * than sent back identical.
 */
export const hasJobDetailChanged = (
  loaded: JobDetailFields,
  current: JobDetailFields
): boolean =>
  loaded.name !== current.name ||
  loaded.additionalNotes !== current.additionalNotes ||
  loaded.invoiceGeneration !== current.invoiceGeneration ||
  loaded.laborGoalHours !== current.laborGoalHours;

/**
 * The server's refusal, plus the advice it gets backwards.
 *
 * When the new pattern would leave the series with no occurrence at all the
 * endpoint keeps the old occurrences and answers "pick another weekday or
 * extend the end date" - which is exactly wrong for the user who just pulled
 * the end date earlier and emptied the series that way. The server's own words
 * are kept verbatim, and the correction is added after them, only for the case
 * that earns it.
 */
export const describeSeriesSaveError = (
  error: any,
  { endDateShortened = false }: { endDateShortened?: boolean } = {}
): string => {
  const message = describePatternSaveError(error);

  if (endDateShortened && /extend the end date/i.test(message)) {
    return `${message} (The end date was moved earlier, so extending it means putting it back - the series kept its existing shifts.)`;
  }

  return message;
};
